import * as React from "react";
import type { Auth, Session } from "./auth";

interface AuthState {
  auth: Auth;
  session: Session | null;
  /** True once the stored session has been read. */
  ready: boolean;
}

const AuthContext = React.createContext<AuthState | null>(null);

export function AuthProvider({ auth, children }: { auth: Auth; children: React.ReactNode }) {
  const [state, setState] = React.useState<{ session: Session | null; ready: boolean }>({ session: null, ready: false });
  React.useEffect(() => {
    let alive = true;
    void auth.getSession().then((session) => alive && setState({ session, ready: true }));
    const off = auth.onChange((session) => setState({ session, ready: true }));
    return () => {
      alive = false;
      off();
    };
  }, [auth]);
  const value = React.useMemo(() => ({ auth, ...state }), [auth, state]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const v = React.useContext(AuthContext);
  if (!v) throw new Error("useAuth needs an AuthProvider");
  return v;
}
